/**
 * A minimal .xlsx writer.
 *
 * The chat's Excel button used to hand out a CSV with a UTF-8 BOM. Excel reads
 * it, but it is not a spreadsheet: every figure arrives as text, so a column of
 * `3.15B` will not sum, sort or chart, and the user's first action is usually to
 * retype it. A real workbook types the cells.
 *
 * Written by hand rather than by adding SheetJS: this bundle is already 4.7 MB
 * and the box that builds it runs out of memory as it is. A workbook is a zip of
 * five small XML parts, and zip entries may be stored uncompressed — so this
 * needs a CRC-32 and nothing else. About 2 KB of code against ~800 KB of
 * dependency.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array): number => {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++)
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};

const utf8 = (s: string) => new TextEncoder().encode(s);

/** XML text escaping. Excel refuses to open a workbook with a bare `&` in it. */
const esc = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Control characters are not legal XML 1.0 and Excel reports the whole
    // file as corrupt rather than skipping them, so stripping them here is the
    // point of this line — the rule is disabled deliberately.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/** Builds a zip with every entry stored, not deflated. */
const zip = (entries: ZipEntry[]): Uint8Array => {
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  const u16 = (v: number) => [v & 0xff, (v >>> 8) & 0xff];
  const u32 = (v: number) => [
    v & 0xff,
    (v >>> 8) & 0xff,
    (v >>> 16) & 0xff,
    (v >>> 24) & 0xff
  ];

  for (const entry of entries) {
    const nameBytes = utf8(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const local = Uint8Array.from([
      ...u32(0x04034b50),
      ...u16(20), // version needed
      ...u16(0x0800), // UTF-8 filename flag
      ...u16(0), // stored
      ...u16(0), // mod time
      ...u16(0), // mod date
      ...u32(crc),
      ...u32(size),
      ...u32(size),
      ...u16(nameBytes.length),
      ...u16(0),
      ...nameBytes
    ]);

    chunks.push(local, entry.data);

    central.push(
      Uint8Array.from([
        ...u32(0x02014b50),
        ...u16(20), // version made by
        ...u16(20), // version needed
        ...u16(0x0800),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(crc),
        ...u32(size),
        ...u32(size),
        ...u16(nameBytes.length),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(0),
        ...u32(offset),
        ...nameBytes
      ])
    );

    offset += local.length + size;
  }

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = Uint8Array.from([
    ...u32(0x06054b50),
    ...u16(0),
    ...u16(0),
    ...u16(entries.length),
    ...u16(entries.length),
    ...u32(centralSize),
    ...u32(offset),
    ...u16(0)
  ]);

  const parts = [...chunks, ...central, end];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
};

export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Spreadsheet column name: 0 -> A, 26 -> AA. */
const columnName = (index: number): string => {
  let name = '';
  let n = index;
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
};

export interface SheetCell {
  /** What the reader sees. */
  text: string;
  /** Present when the cell is a real number; written as a numeric cell. */
  value?: number | null;
}

/**
 * Builds a one-sheet workbook.
 *
 * Row 0 is written as the header (bold, frozen). Cells carrying a `value` are
 * typed as numbers so they sum and sort; everything else is an inline string,
 * which avoids a shared-strings part entirely.
 */
export const buildXlsxBytes = (
  header: string[],
  rows: SheetCell[][],
  sheetName = 'Sheet1'
): Uint8Array => {
  const xmlRows: string[] = [];

  xmlRows.push(
    `<row r="1">${header
      .map(
        (h, c) =>
          `<c r="${columnName(c)}1" t="inlineStr" s="1"><is><t xml:space="preserve">${esc(
            h
          )}</t></is></c>`
      )
      .join('')}</row>`
  );

  rows.forEach((row, r) => {
    const rowNumber = r + 2;
    const cells = row
      .map((cell, c) => {
        const ref = `${columnName(c)}${rowNumber}`;
        if (typeof cell.value === 'number' && Number.isFinite(cell.value)) {
          return `<c r="${ref}"><v>${cell.value}</v></c>`;
        }
        if (!cell.text) return '';
        return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${esc(
          cell.text
        )}</t></is></c>`;
      })
      .join('');
    xmlRows.push(`<row r="${rowNumber}">${cells}</row>`);
  });

  // Excel truncates a sheet name over 31 chars and rejects these characters.
  const safeName =
    esc(sheetName.replace(/[\\/*?:[\]]/g, ' ').slice(0, 31)) || 'Sheet1';

  const sheet =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetViews><sheetView workbookViewId="0">` +
    `<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>` +
    `</sheetView></sheetViews>` +
    `<sheetData>${xmlRows.join('')}</sheetData></worksheet>`;

  const workbook =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets><sheet name="${safeName}" sheetId="1" r:id="rId1"/></sheets></workbook>`;

  const workbookRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
    `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`;

  // Two named styles: 0 is the default, 1 is bold for the header row.
  const styles =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>` +
    `<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>` +
    `<fills count="1"><fill><patternFill patternType="none"/></fill></fills>` +
    `<borders count="1"><border/></borders>` +
    `<cellStyleXfs count="1"><xf/></cellStyleXfs>` +
    `<cellXfs count="2"><xf xfId="0"/><xf fontId="1" applyFont="1" xfId="0"/></cellXfs>` +
    `</styleSheet>`;

  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    `</Types>`;

  const rootRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`;

  return zip([
    { name: '[Content_Types].xml', data: utf8(contentTypes) },
    { name: '_rels/.rels', data: utf8(rootRels) },
    { name: 'xl/workbook.xml', data: utf8(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: utf8(workbookRels) },
    { name: 'xl/styles.xml', data: utf8(styles) },
    { name: 'xl/worksheets/sheet1.xml', data: utf8(sheet) }
  ]);
};

/** The same workbook, wrapped for the browser. */
export const buildXlsx = (
  header: string[],
  rows: SheetCell[][],
  sheetName = 'Sheet1'
): Blob =>
  new Blob([buildXlsxBytes(header, rows, sheetName)], { type: XLSX_MIME });

/** Hands the workbook to the browser as a download. */
export const downloadXlsx = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
